import { Button, ListGroup, Spinner } from 'flowbite-react';
import { Checkbox } from 'flowbite-react';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { verifyOfflineDriveCandidateDocuments } from 'src/apis/offlineDrive';
import { useToast } from 'src/context/ToastProvider';
import { DriveInviteeDocument, OfflineDocumentsTypes } from 'types';

const VerifyDocumentsForm = ({
	driveId,
	candidateId,
	documentsData,
}: {
	driveId: string;
	candidateId: string;
	documentsData: DriveInviteeDocument['documents'];
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [documents, setDocuments] = useState<OfflineDocumentsTypes[]>([]);

	useEffect(() => {
		if (documentsData) {
			setDocuments(
				documentsData
					.filter(doc => doc.isAdminVerified)
					.map(doc => doc.documentType)
					.filter((docType): docType is OfflineDocumentsTypes => docType !== undefined),
			);
		}
	}, [documentsData]);

	const handleCheckboxChange = (document: OfflineDocumentsTypes) => {
		if (documents.includes(document)) {
			setDocuments(documents.filter(doc => doc !== document));
		} else {
			setDocuments([...documents, document]);
		}
	};

	const verifyOfflineDriveCandidateDocumentsFn = useMutation(
		['verifyOfflineDriveCandidateDocuments'],
		verifyOfflineDriveCandidateDocuments,
		{
			onSuccess(data) {
				queryClient.invalidateQueries(['getOfflineDriveCandidates', driveId]);
				queryClient.refetchQueries(['getOfflineDriveCandidates', driveId]);
				toast({
					title: 'Documents verified successfully',
					type: 'success',
				});
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error occured ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		console.log(documents);
		verifyOfflineDriveCandidateDocumentsFn.mutate({ candidateIds: [candidateId], driveId, documents });
	};

	const handleSelectAllDocuments = () => {
		if (documents.length === Object.values(OfflineDocumentsTypes).length) {
			setDocuments([]);
		} else {
			setDocuments(Object.values(OfflineDocumentsTypes));
		}
	};

	return (
		<div>
			<form onSubmit={handleSubmit}>
				<div className="flex flex-col gap-[8px] pb-[20px]">
					<div className="mb-2 block pr-[10px]">
						<span className="text-[#111928] font-inter text-base font-[500] leading-[21px] pr-[5px]">
							Please confirm the items the candidate has brought
						</span>
						<span className="text-red-600 font-inter text-[14px] font-[500] leading-[21px]">*</span>
					</div>

					<div className="flex items-center gap-4">
						<ListGroup className="w-full">
							{Object.values(OfflineDocumentsTypes).map(key => (
								<ListGroup.Item
									key={key}
									theme={{
										link: {
											active: {
												off: 'hover:bg-gray-100 hover:text-blue-700 focus-visible:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:text-white dark:focus-visible:text-white dark:focus-visible:ring-gray-500',
												on: 'bg-blue-700 text-white dark:bg-gray-800',
											},
										},
									}}
									active={documents.includes(key as OfflineDocumentsTypes)}
									onClick={() => handleCheckboxChange(key as OfflineDocumentsTypes)}
								>
									<label htmlFor={key} className="cursor-pointer">
										<div className="flex items-center gap-2 cursor-pointer w-full">
											<Checkbox
												color="purplePlaced"
												id={key}
												checked={documents.includes(key as OfflineDocumentsTypes)}
											/>
											{key.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}
										</div>
									</label>
									<div className="ml-auto flex flex-col gap-2 border-l pl-2 min-w-[150px]">
										<div className="border-b pb-2">
											{documentsData?.find(doc => doc.documentType === key)?.isUserVerified
												? 'User Selected'
												: 'Not Selected'}
										</div>
										<div>
											{documentsData?.find(doc => doc.documentType === key)?.isAdminVerified
												? 'Admin Verified'
												: 'Not Verified'}
										</div>
									</div>
								</ListGroup.Item>
							))}
						</ListGroup>
					</div>

					<div className="flex justify-start items-center gap-2 mt-2 pl-4">
						<Checkbox
							id="select-all"
							color="purplePlaced"
							checked={documents.length === Object.values(OfflineDocumentsTypes).length}
							onChange={() => handleSelectAllDocuments()}
						/>
						<label htmlFor="select-all" className="cursor-pointer">
							Select All
						</label>
					</div>
				</div>
				<Button
					color="purplePlaced"
					disabled={verifyOfflineDriveCandidateDocumentsFn.isLoading}
					type="submit"
					className={'w-full'}
				>
					{verifyOfflineDriveCandidateDocumentsFn.isLoading ? <Spinner /> : 'Submit'}
				</Button>
			</form>
		</div>
	);
};

export default VerifyDocumentsForm;
